import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataGrid, GridActionsCellItem, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
  Tooltip,
} from '@mui/material';

import { BetaBadge } from '@/components/Shared/BetaBadge';
import { PrimaryButton, SecondaryButton } from '@/components/Shared/Buttons';
import { TrblDeleteIcon, TrblEditIcon, TrblLockResetIcon } from '@/components/Icons';

import { SubscriptionDetails, UserAccess } from '../types';

import styles from '../styles.module.scss';

type UsersGridProps = {
  organizationId: string;
  currentUserId: string;
  users: UserAccess[];
  subscription: SubscriptionDetails;
  isUsingSso: boolean;
  isLoading: boolean;
  mcpKeyCounts?: Record<string, number>;
  onToggleAdmin: (userId: string, assign: boolean) => void;
  onToggleSDKAccess: (userId: string, grant: boolean) => void;
  onToggleMfa: (userId: string, enabled: boolean) => void;
  onEditUser: (user: UserAccess) => void;
  onDeleteUser: (userId: string) => void;
  onResendSetPasswordEmail: (userId: string) => void;
};

const gridStyleOverrides: SxProps<Theme> = {
  '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
    outline: 'none !important',
  },
  '& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
};

const ConfirmDialog: FC<{ title?: string; message: string; onConfirm: () => void; onCancel: () => void }> = ({
  title = 'Confirm change',
  message,
  onConfirm,
  onCancel,
}) => (
  <Dialog open onClose={onCancel} className={styles['confirmation-dialog-container']}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText>{message}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <div className={styles['dialog-action-buttons']}>
        <SecondaryButton label="Cancel" onClick={onCancel} width={150} />
        <PrimaryButton label="Confirm" onClick={onConfirm} width={150} />
      </div>
    </DialogActions>
  </Dialog>
);

const RowActions: FC<{
  user: UserAccess;
  isUsingSso: boolean;
  onEditUser: (user: UserAccess) => void;
  onDeleteUser: (userId: string) => void;
  onResendSetPasswordEmail: (userId: string) => void;
}> = ({ user, isUsingSso, onEditUser, onDeleteUser, onResendSetPasswordEmail }) => {
  const [confirming, setConfirming] = useState<'delete' | 'resend' | null>(null);

  return (
    <div className={styles['row-actions-cell']}>
      <Tooltip title="Edit user">
        <GridActionsCellItem icon={<TrblEditIcon />} label="Edit" onClick={() => onEditUser(user)} />
      </Tooltip>
      {!isUsingSso && (
        <Tooltip title="Send set password email">
          <GridActionsCellItem
            icon={<TrblLockResetIcon />}
            label="Send set password email"
            onClick={() => setConfirming('resend')}
          />
        </Tooltip>
      )}
      <Tooltip title="Delete user">
        <GridActionsCellItem icon={<TrblDeleteIcon />} label="Delete" onClick={() => setConfirming('delete')} />
      </Tooltip>
      {confirming === 'delete' && (
        <ConfirmDialog
          title="Confirm delete"
          message={`Are you sure you want to delete the user with email ${user.email}?`}
          onConfirm={() => {
            onDeleteUser(user.id);
            setConfirming(null);
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
      {confirming === 'resend' && (
        <ConfirmDialog
          title="Confirm send"
          message={`Are you sure you want to send a set password email to ${user.email}?`}
          onConfirm={() => {
            onResendSetPasswordEmail(user.id);
            setConfirming(null);
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
};

const ConfirmSelect: FC<{
  value: string;
  options: { label: string; value: string }[];
  confirmMessage: string;
  disabled?: boolean;
  disabledTooltip?: string;
  onChange: (value: string) => void;
}> = ({ value, options, confirmMessage, disabled, disabledTooltip, onChange }) => {
  const [pending, setPending] = useState<string | null>(null);

  const handleChange = (e: SelectChangeEvent<string>) => {
    if (e.target.value !== value) {
      setPending(e.target.value);
    }
  };

  const select = (
    <Select
      size="small"
      value={value}
      disabled={disabled}
      onChange={handleChange}
      sx={{
        fontSize: '12px',
        height: '28px',
        minWidth: 70,
        backgroundColor: 'transparent',
        '.MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#888' },
        '.MuiSvgIcon-root': { color: '#999' },
      }}>
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '12px' }}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <>
      {disabledTooltip && disabled ? <Tooltip title={disabledTooltip}>{select}</Tooltip> : select}
      {pending !== null && (
        <ConfirmDialog
          message={confirmMessage}
          onConfirm={() => {
            onChange(pending);
            setPending(null);
          }}
          onCancel={() => setPending(null)}
        />
      )}
    </>
  );
};

const hasTSDKSeats = (subscription: SubscriptionDetails) => {
  const sdk = subscription.sdkProduct;
  if (!sdk) return false;
  return sdk.activeSeats < sdk.maxSeats;
};

const getUserHasTSDKProductAccess = (user: UserAccess) =>
  user.productAccesses.some((p) => p.productKey === 'TSDK' && p.isEnabled);

const getUserIsAdmin = (user: UserAccess) => user.roles?.includes('Admin') ?? false;

export const UsersGrid: FC<UsersGridProps> = ({
  currentUserId,
  users,
  subscription,
  isUsingSso,
  isLoading,
  mcpKeyCounts,
  onToggleAdmin,
  onToggleSDKAccess,
  onToggleMfa,
  onEditUser,
  onDeleteUser,
  onResendSetPasswordEmail,
}) => {
  const columns: GridColDef<UserAccess>[] = [
    {
      field: 'firstName',
      headerName: 'First name',
      width: 150,
      headerClassName: styles['data-grid-header'],
    },
    {
      field: 'lastName',
      headerName: 'Last name',
      width: 200,
      headerClassName: styles['data-grid-header'],
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      headerClassName: styles['data-grid-header'],
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      disableColumnMenu: true,
      valueGetter: (params) => (getUserIsAdmin(params.row) ? 'Admin' : 'User'),
      renderCell: (params: GridRenderCellParams<UserAccess, string>) => {
        const isSelf = params.row.id === currentUserId;
        const isAdmin = params.value === 'Admin';
        return (
          <ConfirmSelect
            value={isAdmin ? 'admin' : 'user'}
            options={[
              { label: 'Admin', value: 'admin' },
              { label: 'User', value: 'user' },
            ]}
            disabled={isSelf && isAdmin}
            disabledTooltip="You cannot revoke your own admin permissions"
            confirmMessage={
              isAdmin
                ? 'Are you sure you want to revoke Admin permissions for this user?'
                : 'Are you sure you want to grant Admin permissions to this user?'
            }
            onChange={(val) => onToggleAdmin(params.row.id, val === 'admin')}
          />
        );
      },
      headerClassName: styles['data-grid-header'],
    },
    {
      field: 'createdAt',
      headerName: 'Created at',
      type: 'date',
      width: 120,
      valueGetter: (params) => (params.row.createdAt ? new Date(params.row.createdAt) : null),
      renderCell: (params: GridRenderCellParams<UserAccess>) =>
        params.row.createdAt ? dayjs(params.row.createdAt).format('DD/MM/YYYY') : '',
      headerClassName: styles['data-grid-header'],
    },
    {
      field: 'sdkAccess',
      headerName: 'SDK access',
      width: 120,
      disableColumnMenu: true,
      valueGetter: (params) => getUserHasTSDKProductAccess(params.row),
      renderCell: (params: GridRenderCellParams<UserAccess, boolean>) => {
        const cannotEnable = !params.value && !hasTSDKSeats(subscription);
        return (
          <ConfirmSelect
            value={params.value ? 'yes' : 'no'}
            options={[
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ]}
            disabled={cannotEnable}
            disabledTooltip="No available SDK seats"
            confirmMessage={
              params.value
                ? 'Are you sure you want to revoke SDK access for this user?'
                : 'Are you sure you want to grant SDK access to this user?'
            }
            onChange={(val) => onToggleSDKAccess(params.row.id, val === 'yes')}
          />
        );
      },
      headerClassName: styles['data-grid-header'],
    },
    {
      field: 'mfaRequired',
      headerName: 'MFA Required',
      width: 130,
      disableColumnMenu: true,
      valueGetter: (params) => params.row.hasMfaRequirement,
      renderCell: (params: GridRenderCellParams<UserAccess, boolean>) => (
        <ConfirmSelect
          value={params.value ? 'yes' : 'no'}
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ]}
          confirmMessage={
            params.value
              ? 'Are you sure you want to remove MFA requirement for this user?'
              : 'Are you sure you want to require MFA for this user?'
          }
          onChange={(val) => onToggleMfa(params.row.id, val === 'yes')}
        />
      ),
      headerClassName: styles['data-grid-header'],
    },
    ...(mcpKeyCounts
      ? [
          {
            field: 'mcpKeys',
            headerName: 'MCP keys',
            width: 110,
            disableColumnMenu: true,
            valueGetter: (params: { row: UserAccess }) => mcpKeyCounts[params.row.id] ?? 0,
            renderHeader: () => (
              <span>
                MCP keys
                <BetaBadge />
              </span>
            ),
            renderCell: (params: GridRenderCellParams<UserAccess, number>) =>
              params.value ? (
                <Link to={`/mcp?tab=organization&user=${params.row.id}`} style={{ color: '#00f5ba' }}>
                  {params.value}
                </Link>
              ) : (
                <span style={{ color: '#999999' }}>0</span>
              ),
            headerClassName: styles['data-grid-header'],
          } satisfies GridColDef<UserAccess>,
        ]
      : []),
    {
      field: 'actions',
      headerName: '',
      width: 110,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<UserAccess>) => (
        <RowActions
          user={params.row}
          isUsingSso={isUsingSso}
          onEditUser={onEditUser}
          onDeleteUser={onDeleteUser}
          onResendSetPasswordEmail={onResendSetPasswordEmail}
        />
      ),
      headerClassName: styles['data-grid-header'],
    },
  ];

  return (
    <Box component="div" height="100%" width="100%">
      <DataGrid
        sx={gridStyleOverrides}
        rows={users}
        loading={isLoading}
        columns={columns}
        autoPageSize
        disableRowSelectionOnClick
        disableColumnSelector
        rowHeight={42}
        columnHeaderHeight={42}
        initialState={{
          sorting: {
            sortModel: [{ field: 'firstName', sort: 'asc' }],
          },
        }}
        slotProps={{
          row: { className: styles['data-grid-row'] },
        }}
      />
    </Box>
  );
};
